import React, { useEffect, useState } from 'react';
import {
  Button,
  Drawer,
  DrawerProps,
  FormInstance,
  Select,
  Form,
  Spin,
  Checkbox,
} from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { capitalize, omit } from 'lodash';
import { usePageProvider, useMediaQuery } from '../../../hooks';
import {
  notificationFailed,
  notificationSuccess,
} from '../../../../infrastructure';
import { Store } from 'antd/lib/form/interface';

export type ActionProps = 'create' | 'update' | 'duplicate' | 'detail';

interface ConfigAfterAction {
  onReloadTable(): void;
  form: FormInstance;
  isCreateAnother: boolean;
  onClose(payload?: any): void;
}

export interface DrawerFormProps extends DrawerProps {
  action: ActionProps;
  value?: any;
  showDataTypeSelector?: boolean;
  forceRequest?: boolean;
  initialValues?: Store;
  ignoreKeyUpdate?: string[];
  ignoreKeyDuplicate?: string[];
  onValuesChange?(item: any, values: any, form: FormInstance): void;
  handleAfterSave?: (
    response?: any,
    config?: ConfigAfterAction
  ) => Promise<void>;
  onShow: (action: ActionProps) => void;
  functionCreate?: (payload: any, config: ConfigAfterAction) => Promise<void>;
  functionUpdate?: (payload: any, config: ConfigAfterAction) => Promise<void>;
  reloadTable?: () => void;
}

const optionsDataType = [
  { label: 'Original', value: 'original' },
  { label: 'Requested', value: 'requested' },
];

export function DrawerForm(props: DrawerFormProps) {
  const {
    action = 'create',
    value,
    showDataTypeSelector = true,
    forceRequest = false,
    initialValues,
    children,
    ignoreKeyUpdate,
    ignoreKeyDuplicate,
    onValuesChange,
    handleAfterSave,
    onClose,
    functionCreate,
    functionUpdate,
    reloadTable,
  } = props;
  const [form] = Form.useForm();
  const { dataSource, transformer, moduleKey } = usePageProvider();
  const [loadingForm, setLoadingForm] = useState(false);
  const [dataForm, setDataForm] = useState(null);
  const [isCreateAnother, setIsCreateAnother] = useState(false);
  const [dataTypeValue, setDataTypeValue] = useState(
    optionsDataType?.find(item => item?.value === 'original')
  );

  /*Initial data for responsive drawer form*/
  const isTablet = useMediaQuery({ media: 'tablet' });
  const width = props.width ?? 500;
  const responsiveWidth = isTablet ? '100%' : width;

  const isCreate = action === 'create';
  const isUpdate = action === 'update';
  const isDuplicate = action === 'duplicate';

  /*Initial data to set default title drawer form*/
  const title = props.title ?? 'Data';
  const drawerTitle = `${capitalize(action)} ${title}`;

  const InitDrawerProps: DrawerProps = {
    ...omit(props ?? {}, ['value']),
    width: responsiveWidth,
    title: drawerTitle,
  };

  useEffect(() => {
    if (isCreate) {
      form.resetFields();
      form.setFieldsValue({
        ...initialValues,
      });
      setDataForm(null);
    }
  }, [isCreate]);

  useEffect(() => {
    if (isUpdate || isDuplicate) {
      if (value && forceRequest) handleGetData(value?.id);
      else transformerPayload(value);

      //Reset data type to original when data has value
      setDataTypeValue(
        optionsDataType?.find(item => item?.value === 'original')
      );
    }
  }, [
    value,
    forceRequest,
    InitDrawerProps?.open,
    InitDrawerProps?.visible,
    isUpdate,
    isDuplicate,
  ]);

  async function onFinish(payload?: any) {
    await handleSave();
    // if (!isCreateAnother) if (onClose) onClose(undefined);
    // form.resetFields();
  }

  async function create(payload: any): Promise<void> {
    if (functionCreate) {
      await functionCreate(payload, {
        onReloadTable: reloadTable,
        form,
        isCreateAnother,
        onClose,
      });
    } else {
      await dataSource.handleCreate(payload, {
        onSuccess: async ({ response }) => {
          if (handleAfterSave)
            await handleAfterSave(response, {
              onReloadTable: reloadTable,
              form,
              isCreateAnother,
              onClose,
            });
          notificationSuccess(['Successfully created data.']);
          if (!isCreateAnother) if (onClose) onClose(undefined);
          form.resetFields();
        },
        onFailed: ({ message }: any) => {
          notificationFailed(message);
        },
      });
    }
  }

  async function update(payload: any): Promise<void> {
    if (functionUpdate) {
      await functionUpdate(payload, {
        onReloadTable: reloadTable,
        form,
        isCreateAnother,
        onClose,
      });
    } else {
      await dataSource.handleUpdate(value?.id, payload, {
        onSuccess: async ({ response }) => {
          if (handleAfterSave)
            await handleAfterSave(response, {
              onReloadTable: reloadTable,
              form,
              isCreateAnother,
              onClose,
            });
          notificationSuccess(['Successfully updated data.']);
          if (!isCreateAnother) if (onClose) onClose(undefined);
          form.resetFields();
        },
        onFailed: ({ message }: any) => {
          notificationFailed(message);
        },
      });
    }
  }

  function transformerGetData(payload: any) {
    if (transformer?.transformerGetData)
      return transformer.transformerGetData(payload);
    return payload;
  }

  function transformerCreate(payload: any) {
    if (transformer?.transformerCreate)
      return transformer.transformerCreate(payload);
    return payload;
  }

  function transformerUpdate(payload: any) {
    if (transformer?.transformerUpdate)
      return transformer.transformerUpdate(payload);
    return payload;
  }

  function transformerDuplicate(payload: any) {
    if (transformer?.transformerDuplicate)
      return transformer.transformerDuplicate(payload);
    return payload;
  }

  async function handleSave(): Promise<void> {
    try {
      const values = await form.validateFields();
      let payload = values;
      if (isUpdate) payload = transformerUpdate(payload);
      else if (isCreate) payload = transformerCreate(payload);
      else if (isDuplicate) payload = transformerDuplicate(payload);

      const cleanVal = omit(payload, ['id', 'form_action']);
      if (isUpdate) await update(cleanVal);
      else await create(cleanVal);
    } catch (error) {
      notificationFailed(['Unexpected error, try again.']);
      console.log(error);
    }
  }

  async function onReset() {
    await form.resetFields();
    const fields = form.getFieldsValue();
    const payload = {};
    for (const key of Object.keys(fields)) {
      Object.assign(payload, { [key]: undefined });
    }
  }

  function makeDataForm(payload: any): void {
    let formPayload = payload;
    if (isUpdate) formPayload = omit(formPayload, ignoreKeyUpdate);
    else if (isDuplicate) formPayload = omit(formPayload, ignoreKeyDuplicate);
    form.setFieldsValue({ form_action: action, ...(formPayload ?? {}) });
    setDataForm(formPayload);
  }

  function transformerPayload(payload: any): void {
    let resultPayload = payload;
    if (transformerGetData)
      resultPayload = transformer.transformerGetData(payload);
    makeDataForm(resultPayload);
  }

  async function handleGetData(id: string): Promise<void> {
    setLoadingForm(true);
    await dataSource.handleGetData(id, {
      onSuccess: ({ response }: any) => {
        transformerPayload(response);
      },
      onFailed: ({ message }: any) => {
        notificationFailed(message);
      },
    });
    setLoadingForm(false);
  }

  function FooterDrawer() {
    return (
      <div
        style={{
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <Checkbox
            checked={action === 'update' ? false : isCreateAnother}
            onChange={e => setIsCreateAnother(e.target.checked)}
            {...(action === 'update' ? { style: { display: 'none' } } : {})}
          >
            Create another
          </Checkbox>
        </div>
        <div>
          <Button
            style={{ marginRight: '5px' }}
            onClick={() => {
              onReset();
              if (onClose) onClose(undefined);
            }}
          >
            Cancel
          </Button>
          <Button type="primary" onClick={() => form.submit()}>
            Save
          </Button>
        </div>
      </div>
    );
  }

  function handleChangeDataType(row: any): void {
    let selectedDataType = optionsDataType?.find(
      item => item?.value === 'original'
    );

    //Initial original and requested data
    const originalData = value;
    const requestedData = dataForm?.requested_data;

    if (row)
      selectedDataType = optionsDataType?.find(item => item?.value === row);
    if (row === 'requested') transformerPayload(requestedData);
    else transformerPayload(originalData);
    setDataTypeValue(selectedDataType);
  }

  function ExtraDrawer() {
    return (
      showDataTypeSelector &&
      !value?.has_requested_process &&
      !!value?.requested_data && (
        <Select
          value={dataTypeValue}
          onChange={handleChangeDataType}
          style={{ width: '120px' }}
          options={optionsDataType}
        />
      )
    );
  }

  return (
    <Drawer
      style={{ overflow: 'hidden' }}
      {...InitDrawerProps}
      footer={<FooterDrawer />}
      footerStyle={{ height: '50px' }}
      extra={<ExtraDrawer />}
      destroyOnClose={true}
    >
      <Spin
        spinning={loadingForm}
        indicator={<LoadingOutlined />}
        style={{ minHeight: '50vh' }}
      >
        <Form
          form={form}
          layout="vertical"
          name={`drawer-form-${moduleKey}`}
          scrollToFirstError
          onFinish={onFinish}
          onValuesChange={(item, values) => {
            if (onValuesChange) onValuesChange(item, values, form);
          }}
          initialValues={initialValues}
        >
          <Form.Item name="form_action" noStyle />
          {children}
        </Form>
      </Spin>
    </Drawer>
  );
}
